/**
 * Pipedrive Custom Fields Configuration
 * Maps custom field names to their Pipedrive field IDs
 */

export interface PipedriveCustomField {
  id: string;
  name: string;
  description?: string;
}

export interface PipedriveCustomFieldsConfig {
  // Deal custom fields
  deal: {
    clientLocation: PipedriveCustomField;
    dealType: PipedriveCustomField;
    speakerType: PipedriveCustomField;
    sessionTopic: PipedriveCustomField;
    proposalLink: PipedriveCustomField;
    proposedEventDate: PipedriveCustomField;
  };
  // Person custom fields (if any)
  person: Record<string, PipedriveCustomField>;
  // Organization custom fields
  organization: {
    summary: PipedriveCustomField;
    companyPhilosophy: PipedriveCustomField;
    recentActivitiesCulture: PipedriveCustomField;
    prNews: PipedriveCustomField;
    dealTopics: PipedriveCustomField;
  };
}

/**
 * Strongly typed organization custom fields result
 * Matches the fields defined in PipedriveCustomFieldsConfig['organization']
 */
export interface OrganizationCustomFields {
  // Standard fields
  companyName: string | null;
  employeeCount: number | null;
  address: string | null;
  website: string | null;
  // Custom fields
  summary: string | null;
  companyPhilosophy: string | null;
  recentActivitiesCulture: string | null;
  prNews: string | null;
  dealTopics: number[] | null;
}

/**
 * Type helper to get organization custom field names
 */
export type OrganizationCustomFieldName =
  keyof PipedriveCustomFieldsConfig['organization'];

/**
 * Pipedrive custom fields configuration for Peptalk
 * These field IDs are specific to the Peptalk Pipedrive instance
 */
//TODO [PTPRO-369]: add all custom fields
export const PIPEDRIVE_CUSTOM_FIELDS: PipedriveCustomFieldsConfig = {
  deal: {
    clientLocation: {
      id: '44be54b2bcca9613b61fcf598b9424c7face4c8b',
      name: 'Client Location',
      description: 'Location where the client is based',
    },
    dealType: {
      id: '60abe02f8d65d04ba7af50f36b6c8d1ff18ac0dc',
      name: 'Deal Type',
      description: 'Type of deal (e.g., Speaking Engagement, Workshop, etc.)',
    },
    speakerType: {
      id: '4c68379eee984826b4d72cc2a703eda884b952ee',
      name: 'Speaker Type',
      description:
        'Type of speaker (e.g., Keynote Speaker, Workshop Facilitator, etc.)',
    },
    sessionTopic: {
      id: 'ab1c074d230c2252f360a678dae5569b28c7724e',
      name: 'Session Topic',
      description: 'Main topic or theme of the session',
    },
    proposalLink: {
      id: '8dc013eae12dbb80e574653c95240a1f2e4ff799',
      name: 'Proposal Link',
      description: 'Link to the proposal document or page',
    },
    proposedEventDate: {
      id: 'd8dc3d1cd6d9c238eefaf553cf8f8171be49c6be',
      name: 'Proposed Event Date',
      description: 'The proposed date for the event',
    },
  },
  person: {
    // Add person custom fields here as needed
  },
  organization: {
    summary: {
      id: 'd2b749805cb9e49bc7ae087b1b4713e148ab2a2e',
      name: 'Summary',
      description:
        'Company overview/summary (maps to Pipedrive "Summary" field)',
    },
    companyPhilosophy: {
      id: '46c442d9fd74e5d9d68a4ea1a3d7e41f1f906477',
      name: 'Company Philosophy',
      description:
        'Company philosophy and values (maps to "Values / Philosophy" field)',
    },
    recentActivitiesCulture: {
      id: '98ee187a1932595af4183788c21ddb29b50fe221',
      name: 'Recent Activities & Culture',
      description:
        'Recent activities and company culture information (maps to "Events Held" field)',
    },
    prNews: {
      id: '89bd98bb23677bdf6bc491e34c8a910e7234692d',
      name: 'PR/News',
      description: 'Recent PR and news about the company',
    },
    dealTopics: {
      id: '01bb1db54127a577e9080cd95b9551dbabb84388',
      name: 'Deal Topics',
      description: 'Topics of interest for speaker matching (multi-select)',
    },
  },
};

/**
 * Helper function to get custom field ID by name
 * Uses function overloads for type-safe field name access
 */

// Overload for deal fields
export function getCustomFieldId(
  entity: 'deal',
  fieldName: keyof PipedriveCustomFieldsConfig['deal']
): string | undefined;
// Overload for organization fields
export function getCustomFieldId(
  entity: 'organization',
  fieldName: keyof PipedriveCustomFieldsConfig['organization']
): string | undefined;
// Overload for person fields (fallback to string)
export function getCustomFieldId(
  entity: 'person',
  fieldName: string
): string | undefined;
// Implementation
export function getCustomFieldId(
  entity: 'deal' | 'person' | 'organization',
  fieldName: string
): string | undefined {
  if (entity === 'deal') {
    const config = PIPEDRIVE_CUSTOM_FIELDS.deal;
    const dealField =
      config[fieldName as keyof PipedriveCustomFieldsConfig['deal']];
    return dealField?.id;
  }

  if (entity === 'organization') {
    const config = PIPEDRIVE_CUSTOM_FIELDS.organization;
    const orgField =
      config[fieldName as keyof PipedriveCustomFieldsConfig['organization']];
    return orgField?.id;
  }

  // For person, fieldName is a string key
  const config = PIPEDRIVE_CUSTOM_FIELDS.person;
  const customField = config[fieldName];
  return customField?.id;
}

/**
 * Helper function to get all custom field IDs for an entity
 */
export function getCustomFieldIds(
  entity: 'deal' | 'person' | 'organization'
): string[] {
  const config = PIPEDRIVE_CUSTOM_FIELDS[entity];
  return Object.values(config).map(field => field.id);
}

/**
 * Helper function to create custom field data for API calls
 */
export function createCustomFieldData(
  entity: 'deal' | 'person' | 'organization',
  fieldName: string,
  value: string | number | boolean
): Record<string, unknown> {
  // Use type assertion to satisfy overloads - the implementation handles all cases
  const fieldId = getCustomFieldId(entity as any, fieldName as any);
  if (!fieldId) {
    throw new Error(
      `Custom field '${fieldName}' not found for entity '${entity}'`
    );
  }

  return {
    [fieldId]: value,
  };
}

/**
 * Helper function to create multiple custom field data for API calls
 */
export function createMultipleCustomFieldData(
  entity: 'deal' | 'person' | 'organization',
  fields: Record<string, string | number | boolean>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [fieldName, value] of Object.entries(fields)) {
    // Use type assertion to satisfy overloads - the implementation handles all cases
    const fieldId = getCustomFieldId(entity as any, fieldName as any);
    if (fieldId) {
      result[fieldId] = value;
    } else {
      console.warn(
        `Custom field '${fieldName}' not found for entity '${entity}'`
      );
    }
  }

  return result;
}
